import React, { useCallback } from 'react';
import styled from 'styled-components';
import Table from 'src/components/Table/Table';
import { useAppDispatch, useAppSelector } from '#app/hooks/useAppStore';
import { ContainerIconWrapper, Ticker } from 'src/pages/Investing/Invests/components/styles';
import { BodyBold, Caption } from 'src/components/Text/styles';
import { Button } from 'finform';
import { Money } from 'finhelper';
import {
  extractRepoOperationAmount,
  getRepoMoneyAmountDisplayLabel,
} from '#app/utils/repoCurrency';
import DefaultIcon from '#app/assets/icons/default.svg';
import {
  setRepoForm,
  setRepoCloseForm,
  type RepoFormPayload,
  type RepoCloseFormPayload,
} from '#app/actions/repo';
import {
  Container as TableContainer,
  Head as TableHead,
  HeadWrapper as TableHeadWrapper,
  ThMain as TableThMain,
} from '#app/components/Table/styles';
import { RepoData } from '#app/pages/Relations/Repos/types/repoTypes';
import { resolveRepoPaperDisplayName } from '#app/pages/Relations/Repos/utils/repoPaperDisplayName';
import { DateFormats, BACKEND_DATE_FORMATS } from '#app/helpers/DateFormats';
import moment from 'moment/moment';
import RepoLockIcon from './RepoLockIcon';

const StickyHeaderWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  background: #fff;
`;
export enum ReposTableType {
  OPENED = 1, // Открытые REPO
  CLOSED = 2, // Закрытые REPO
}

interface ReposTableOwnProps {
  id: string;
  items: RepoData[];
  tableType?: ReposTableType; // Тип таблицы: 1 - открытые, 2 - закрытые
  currency?: {
    id: string;
    code?: string;
    sign?: string;
  } | null;
  stickyHeader?: boolean;
}

interface ReposTableProps extends ReposTableOwnProps {
  i18n?: {
    common?: {
      quantity?: string;
    };
    invest?: {
      blockedQuantity?: string;
      unblockedQuantity?: string;
      repoOpeningDate?: string;
      repoCalculateDate?: string;
      repoCloseDate?: string;
      repoCloseCalculateDate?: string;
      ratePercent?: string;
      amount?: string;
      repoCommissionSum?: string;
      discount?: string;
      close?: string;
      // Переводы для колонок таблицы (без слова "РЕПО")
      tableOpeningDate?: string;
      tableCalculateDate?: string;
      tableCloseDate?: string;
      tableCloseCalculateDate?: string;
      tableDealSum?: string;
      repoTableAmount?: string;
      repoTableDealSum?: string;
    };
  };
}

const ReposTable: React.FC<ReposTableProps> = (props) => {
  const i18n = useAppSelector((state: any) => state.i18n);
  const amountColumnLabel = i18n?.invest?.repoTableAmount ?? 'Сумма (вал. сделки)';
  const dealSumColumnLabel = i18n?.invest?.repoTableDealSum ?? 'Сумма сделки (вал. сделки)';
  const dispatch = useAppDispatch();
  const setRepoFormFn = useCallback(
    (form: RepoFormPayload) => dispatch(setRepoForm(form)),
    [dispatch]
  );
  const setRepoCloseFormFn = useCallback(
    (form: RepoCloseFormPayload) => dispatch(setRepoCloseForm(form)),
    [dispatch]
  );
  const tableType = props.tableType || ReposTableType.OPENED;
  const isClosedTable = tableType === ReposTableType.CLOSED;

  // Обработчик клика на строку - открывает модальное окно редактирования
  const handleRowClick = useCallback(
    (item: RepoData) => {
      const name = resolveRepoPaperDisplayName(item);
      setRepoFormFn({
        ...item,
        name: name,
        ticker_hub_id: item?.hub?.link?.ticker_hub_id || null,
      });
    },
    [setRepoFormFn]
  );

  const renderButtons = (repo: RepoData) => {
    // Для закрытых REPO кнопка "Закрыть" не показывается
    if (isClosedTable) {
      return null;
    }

    return (
      // @ts-ignore
      <Button
        narrow
        // @ts-ignore
        style={{
          background: '#EF5E70',
          marginRight: 10,
          width: 96,
          height: 24,
        }}
        onClick={(e) => {
          e.stopPropagation();
          const name = resolveRepoPaperDisplayName(repo);
          setRepoCloseFormFn({
            ...repo,
            name: name,
            ticker_hub_id: repo?.hub?.link?.ticker_hub_id || null,
          });
        }}
      >
        {i18n?.invest?.close || 'Закрыть'}
      </Button>
    );
  };

  return (
    <>
      {/* Заголовки таблицы для REPO */}
      <TableContainer>
        <TableHead>
          <TableHeadWrapper style={{ border: 'none' }}>
            {/* @ts-ignore */}
            <TableThMain flexWidth={'12%'} fontSize="12px" className="black">
              {/* @ts-ignore */}
              <Caption grey>{i18n?.invest?.blockedQuantity || 'Кол-во заблок.'}</Caption>
              {/* @ts-ignore */}
            </TableThMain>
            {isClosedTable && (
              <>
                {/* @ts-ignore */}
                <TableThMain flexWidth={'12%'} fontSize="12px" className="black">
                  {/* @ts-ignore */}
                  <Caption grey>{i18n?.invest?.unblockedQuantity || 'Кол-во разблок.'}</Caption>
                </TableThMain>
              </>
            )}
            {!isClosedTable ? (
              <>
                {/* @ts-ignore */}
                <TableThMain flexWidth="18%" fontSize="12px" className="black">
                  {/* @ts-ignore */}
                  <Caption grey>{i18n?.invest?.tableOpeningDate || 'Дата открытия'}</Caption>
                </TableThMain>
                {/* @ts-ignore */}
                <TableThMain flexWidth="18%" fontSize="12px" className="black">
                  {/* @ts-ignore */}
                  <Caption grey>{i18n?.invest?.tableCalculateDate || 'Дата расчетов'}</Caption>
                </TableThMain>
              </>
            ) : (
              <>
                {/* @ts-ignore */}
                <TableThMain flexWidth="15%" fontSize="12px" className="black">
                  {/* @ts-ignore */}
                  <Caption grey>{i18n?.invest?.tableCloseDate || 'Дата закрытия сделки'}</Caption>
                </TableThMain>
                {/* @ts-ignore */}
                <TableThMain flexWidth="15%" fontSize="12px" className="black">
                  {/* @ts-ignore */}
                  <Caption grey>
                    {i18n?.invest?.tableCloseCalculateDate || 'Дата закрытия расчетов'}
                  </Caption>
                </TableThMain>
              </>
            )}
            {/* @ts-ignore */}
            <TableThMain flexWidth={isClosedTable ? '8%' : '15%'} fontSize="12px" className="black">
              {/* @ts-ignore */}
              <Caption grey>{i18n?.invest?.ratePercent || 'Ставка %'}</Caption>
            </TableThMain>
            {/* @ts-ignore */}
            <TableThMain
              flexWidth={isClosedTable ? '15%' : '20%'}
              fontSize="12px"
              className="black"
            >
              {/* @ts-ignore */}
              <Caption grey>{amountColumnLabel}</Caption>
              {/* @ts-ignore */}
            </TableThMain>
            {isClosedTable && (
              <>
                {/* @ts-ignore */}
                <TableThMain flexWidth="15%" fontSize="12px" className="black">
                  {/* @ts-ignore */}
                  <Caption grey>{dealSumColumnLabel}</Caption>
                </TableThMain>
              </>
            )}
            {/* @ts-ignore */}
            <TableThMain flexWidth={isClosedTable ? '8%' : '17%'} fontSize="12px" className="black">
              {/* @ts-ignore */}
              <Caption grey>{i18n?.invest?.discount || 'Дисконт %'}</Caption>
            </TableThMain>
          </TableHeadWrapper>
        </TableHead>
      </TableContainer>

      {/* @ts-ignore */}
      <Table
        // @ts-ignore
        id={props.id + '-repo'}
        // @ts-ignore
        hideHeader={true}
        // @ts-ignore
        rowStyle={{ flexWrap: 'wrap' }}
        // @ts-ignore
        noDataText={false}
        // @ts-ignore
        onRowClick={handleRowClick}
        // @ts-ignore
        fields={{
          Наименование: {
            name: 'Наименование',
            width: '100%',
            hideHeader: true,
            field: (item: RepoData, _key: string) => {
              const name = resolveRepoPaperDisplayName(item);
              const shortName = name.length > 38 ? name.slice(0, 39) + '...' : name;
              const itemType = item?.hub?.type || 'repo';

              return (
                <Ticker
                  id={`repo-${item.id}`}
                  data-item-id={item.catalog_hub_id}
                  data-item-type={String(itemType)}
                >
                  <div>
                    <ContainerIconWrapper>
                      {!isClosedTable && <RepoLockIcon />}
                      <img
                        className="icon"
                        src={item?.hub?.link?.catalogItem?.icon || DefaultIcon}
                        alt=""
                      />
                    </ContainerIconWrapper>
                    <div>
                      <BodyBold title={name}>{shortName}</BodyBold>
                    </div>
                    <div onClick={(e) => e.stopPropagation()}>{renderButtons(item)}</div>
                  </div>
                </Ticker>
              );
            },
          },
          [i18n?.invest?.blockedQuantity || 'Кол-во заблок.']: {
            name: i18n?.invest?.blockedQuantity || 'Кол-во заблок.',
            width: '12%',
            field: (item: RepoData, _key: string) => {
              // Для закрытых REPO показываем заблокированное количество (opened_quantity)
              // Для открытых REPO показываем opened_quantity
              const quantity = isClosedTable
                ? Money.format(item.opened_quantity || 0)
                : Money.format(item.opened_quantity || 0);
              // @ts-ignore
              return <Caption black>{quantity || '-'}</Caption>;
            },
          },
          ...(isClosedTable
            ? {
                [i18n?.invest?.unblockedQuantity || 'Кол-во разблок.']: {
                  name: i18n?.invest?.unblockedQuantity || 'Кол-во разблок.',
                  width: '12%',
                  field: (item: RepoData) => {
                    const unblockedQuantity = Money.format(item.closed_quantity || 0);
                    // @ts-ignore
                    return <Caption black>{unblockedQuantity || '-'}</Caption>;
                  },
                },
              }
            : {}),
          ...(!isClosedTable
            ? {
                [i18n?.invest?.tableOpeningDate || 'Дата открытия']: {
                  name: i18n?.invest?.tableOpeningDate || 'Дата открытия',
                  width: '18%',
                  field: (item: RepoData) => {
                    const parsedDate = moment(item.opened_at, BACKEND_DATE_FORMATS, true);
                    // @ts-ignore
                    return (
                      // @ts-ignore
                      <Caption black>
                        {parsedDate.isValid() ? parsedDate.format(DateFormats.USER_DATE) : '-'}
                      </Caption>
                    );
                  },
                },
                [i18n?.invest?.tableCalculateDate || 'Дата расчетов']: {
                  name: i18n?.invest?.tableCalculateDate || 'Дата расчетов',
                  width: '18%',
                  field: (item: RepoData) => {
                    const parsedDate = moment(item.opened_calculate_at, BACKEND_DATE_FORMATS, true);
                    // @ts-ignore
                    return (
                      // @ts-ignore
                      <Caption black>
                        {parsedDate.isValid() ? parsedDate.format(DateFormats.USER_DATE) : '-'}
                      </Caption>
                    );
                  },
                },
              }
            : {
                [i18n?.invest?.tableCloseDate || 'Дата закрытия сделки']: {
                  name: i18n?.invest?.tableCloseDate || 'Дата закрытия сделки',
                  width: '15%',
                  field: (item: RepoData) => {
                    const parsedDate = moment(item.closed_at, BACKEND_DATE_FORMATS, true);
                    // @ts-ignore
                    return (
                      // @ts-ignore
                      <Caption black>
                        {parsedDate.isValid() ? parsedDate.format(DateFormats.USER_DATE) : '-'}
                      </Caption>
                    );
                  },
                },
                [i18n?.invest?.tableCloseCalculateDate || 'Дата закрытия расчетов']: {
                  name: i18n?.invest?.tableCloseCalculateDate || 'Дата закрытия расчетов',
                  width: '15%',
                  field: (item: RepoData) => {
                    const parsedDate = moment(item.closed_calculate_at, BACKEND_DATE_FORMATS, true);
                    // @ts-ignore
                    return (
                      // @ts-ignore
                      <Caption black>
                        {parsedDate.isValid() ? parsedDate.format(DateFormats.USER_DATE) : '-'}
                      </Caption>
                    );
                  },
                },
              }),
          [i18n?.invest?.ratePercent || 'Ставка %']: {
            name: i18n?.invest?.ratePercent || 'Ставка %',
            width: isClosedTable ? '8%' : '15%',
            field: (item: RepoData) => {
              if (item.percent != null && item.percent !== '') {
                const percentValue =
                  typeof item.percent === 'number' ? item.percent : parseFloat(item.percent);
                // @ts-ignore
                return (
                  // @ts-ignore
                  <Caption black>
                    {!isNaN(percentValue) ? `${percentValue.toFixed(2)}%` : '-'}
                  </Caption>
                );
              }
              // @ts-ignore
              return <Caption black>-</Caption>;
            },
          },
          [amountColumnLabel]: {
            name: amountColumnLabel,
            width: isClosedTable ? '15%' : '20%',
            field: (item: RepoData) => {
              const sumValue = extractRepoOperationAmount(item);
              const currencyLabel = getRepoMoneyAmountDisplayLabel(
                item.operation_sum,
                item.to_currency_id
              );
              // @ts-ignore
              return (
                // @ts-ignore
                <Caption black>
                  {sumValue > 0 ? Money.format(sumValue, 2) + ' ' + currencyLabel : '-'}
                </Caption>
              );
            },
          },
          ...(isClosedTable
            ? {
                [dealSumColumnLabel]: {
                  name: dealSumColumnLabel,
                  width: '15%',
                  field: (item: RepoData) => {
                    const commissionSumRaw = item.commission_sum;
                    const sumValue = Money.toDigits(
                      commissionSumRaw != null &&
                        typeof commissionSumRaw === 'object' &&
                        'amount' in commissionSumRaw
                        ? commissionSumRaw.amount
                        : (commissionSumRaw ?? 0)
                    );
                    const currencyLabel = getRepoMoneyAmountDisplayLabel(
                      item.commission_sum,
                      item.from_currency_id
                    );
                    // @ts-ignore
                    return (
                      // @ts-ignore
                      <Caption black>
                        {sumValue > 0 ? Money.format(sumValue, 2) + ' ' + currencyLabel : '-'}
                      </Caption>
                    );
                  },
                },
              }
            : {}),
          [i18n?.invest?.discount || 'Дисконт %']: {
            name: i18n?.invest?.discount || 'Дисконт %',
            width: isClosedTable ? '8%' : '17%',
            field: (item: RepoData) => {
              if (item.discount != null && item.discount !== '') {
                const discountValue =
                  typeof item.discount === 'number'
                    ? parseFloat(String(item.discount))
                    : parseFloat(item.discount);
                // @ts-ignore
                return (
                  // @ts-ignore
                  <Caption black>
                    {!isNaN(discountValue) ? `${discountValue.toFixed(2)}%` : '-'}
                  </Caption>
                );
              }
              // @ts-ignore
              return <Caption black>-</Caption>;
            },
          },
        }}
        items={props.items}
      />
    </>
  );
};

export default ReposTable;
